import { planeMoments } from './dynamics'
import type { Side } from './gait'
import type { Simulation } from './simulation'

/**
 * Der Zeitschrieb — was über mehrere Zyklen passiert.
 *
 * WAS HIER ANDERS IST ALS AUF DER SIMULATIONSSEITE: dort steht ein
 * geschlossener Zyklus, hier läuft die Zeit. Der Unterschied ist nicht die
 * Rechnung — es ist dieselbe — sondern die Achse. Ein Zyklus zeigt, wie eine
 * Bewegung AUSSIEHT; ein Zeitschrieb zeigt, was passiert, wenn man mitten
 * darin etwas ändert.
 *
 * Und genau das ist der Zweck. Wer vom Gehen aufs Laufen umschaltet, sieht im
 * Zyklusbild zwei getrennte Kurven. Im Zeitschrieb sieht er den Übergang: wo
 * die Kadenz springt, wie das Kniemoment nachzieht, ob der Sprung im
 * Bodenkontakt liegt oder in der Schwungphase.
 *
 * DER RINGSPEICHER. Ein Zeitschrieb läuft, bis jemand ihn anhält — eine Liste,
 * die dabei wächst, ist ein Speicherleck mit Bedienoberfläche. Der Puffer hat
 * deshalb eine feste Grösse und überschreibt den ältesten Wert. Was aus dem
 * Fenster gelaufen ist, ist weg; das ist die ehrliche Zusage.
 */

/* -------------------------------------------------------------------------
   Was aufgezeichnet werden kann
   ------------------------------------------------------------------------- */

export interface RecorderChannel {
  key: string
  label: string
  unit: string
  /** Welche der sechs Serienfarben. */
  tone: 1 | 2 | 3 | 4 | 5 | 6
  /**
   * Die Werte über den Zyklus, aus dem laufenden Ergebnis.
   *
   * `null` heisst: gibt es in diesem Zustand nicht. Eine Achse ohne
   * Winkelfreiheit hat kein Moment, das ein Antrieb aufbringt — sie liefert
   * dann nichts, statt eine Null vorzutäuschen.
   */
  read: (simulation: Simulation) => Float32Array | null
}

const moment = (joint: 'hip' | 'knee' | 'ankle', side: Side) => (simulation: Simulation) =>
  planeMoments(simulation.dynamics, joint, side).sag

const angle =
  (joint: 'hip' | 'knee' | 'ankle', side: Side) =>
  (simulation: Simulation): Float32Array | null =>
    simulation.gait.angles[joint]?.sag?.[side] ?? null

/**
 * Der Kanalkatalog.
 *
 * Bewusst KLEIN gehalten. Ein Zeitschrieb mit dreissig Kurven ist kein
 * Werkzeug, sondern ein Vorwurf; die sechs hier beantworten die Fragen, für
 * die man überhaupt einen Zeitschrieb aufmacht — Winkel, Moment, Bodenkraft
 * und Lastverteilung. Wer mehr braucht, ergänzt eine Zeile.
 */
export const RECORDER_CHANNELS: RecorderChannel[] = [
  { key: 'hip.angle', label: 'Hüfte · Winkel', unit: '°', tone: 1, read: angle('hip', 'R') },
  { key: 'knee.angle', label: 'Knie · Winkel', unit: '°', tone: 5, read: angle('knee', 'R') },
  {
    key: 'ankle.angle',
    label: 'Sprunggelenk · Winkel',
    unit: '°',
    tone: 3,
    read: angle('ankle', 'R'),
  },
  { key: 'hip.moment', label: 'Hüfte · Moment', unit: 'Nm', tone: 1, read: moment('hip', 'R') },
  { key: 'knee.moment', label: 'Knie · Moment', unit: 'Nm', tone: 5, read: moment('knee', 'R') },
  {
    key: 'ankle.moment',
    label: 'Sprunggelenk · Moment',
    unit: 'Nm',
    tone: 3,
    read: moment('ankle', 'R'),
  },
  {
    key: 'grf',
    label: 'Bodenreaktionskraft',
    unit: '× KG',
    tone: 4,
    read: (simulation) => {
      const { dynamics } = simulation
      const weight = dynamics.mass * 9.80665
      const out = new Float32Array(dynamics.samples)
      for (let i = 0; i < dynamics.samples; i++) {
        out[i] = (dynamics.grf.R[i].y + dynamics.grf.L[i].y) / weight
      }
      return out
    },
  },
  {
    key: 'load',
    label: 'Last auf dem rechten Fuss',
    unit: '',
    tone: 2,
    read: (simulation) => Float32Array.from(simulation.dynamics.load.R),
  },
]

/* -------------------------------------------------------------------------
   Zyklisch abtasten
   ------------------------------------------------------------------------- */

/**
 * Linear interpolierter Wert an einer Phase.
 *
 * `period` ist die Zahl der Abtastschritte JE ZYKLUS — nicht unbedingt die
 * Länge der Reihe.
 *
 * DER UNTERSCHIED HAT MICH EINEN FEHLER GEKOSTET. Die Winkelbahnen des Gangs
 * tragen den Schlusspunkt mit: 121 Werte für 120 Schritte, wobei der letzte
 * gleich dem ersten ist. Die Momente tragen ihn nicht: 120 Werte für 120
 * Schritte. Wer beide gleich behandelt, tastet die Winkel um ein
 * Hundertzwanzigstel zu langsam ab — der Zeitschrieb läuft dann sichtbar aus
 * dem Takt mit den Momenten daneben.
 *
 * Mit der richtigen Periode fällt der Fall automatisch zusammen: bei einer
 * geschlossenen Reihe ist der Nachbar des letzten Schritts der Schlusspunkt,
 * bei einer offenen der Anfang.
 */
export function sampleAt(values: Float32Array, phase: number, period = values.length): number {
  const n = values.length
  if (n === 0) return NaN
  if (n === 1) return values[0]

  const steps = Math.max(1, Math.min(period, n))
  const q = ((phase % 1) + 1) % 1
  const x = q * steps
  const i = Math.floor(x) % steps
  const t = x - Math.floor(x)
  return values[i] * (1 - t) + values[(i + 1) % n] * t
}

/* -------------------------------------------------------------------------
   Der Ringspeicher
   ------------------------------------------------------------------------- */

/** Ein Ereignis im Schrieb — ein Zyklusanfang oder eine Änderung durch den Anwender. */
export interface TraceMark {
  /** Absolute Zeit, s. */
  time: number
  kind: 'cycle' | 'change'
  label?: string
}

export interface TraceWindow {
  /** Anfang und Ende des Fensters, s. */
  from: number
  to: number
  /** Werte je Kanal, gleichmässig über das Fenster verteilt. NaN wo nichts steht. */
  series: Record<string, Float32Array>
  /** Wie viele Punkte je Kanal. */
  samples: number
  /** Die Ereignisse im Fenster, mit ihrer Lage als Anteil 0 … 1. */
  marks: { at: number; kind: TraceMark['kind']; label?: string }[]
}

/**
 * Fester Puffer über der Zeit.
 *
 * Nicht `Array.push`: bei 50 Hz und einer Viertelstunde Aufzeichnung wären das
 * 45 000 Objekte je Kanal. Der Ring hält die Zahl konstant und die Zuteilung
 * bei null.
 */
export class TraceBuffer {
  readonly capacity: number
  private readonly time: Float64Array
  private readonly data = new Map<string, Float32Array>()
  private head = 0
  private filled = 0
  private lastPhase = 0
  /** Die letzte aufgezeichnete Zeit, s. */
  now = 0
  marks: TraceMark[] = []

  /**
   * `capacity` in Abtastpunkten. Bei 50 Hz sind 3000 Punkte eine Minute — das
   * ist reichlich für die Fenster, die die Seite anbietet (2 bis 16 s), und
   * lässt Luft, um im angehaltenen Schrieb zurückzublättern.
   */
  constructor(capacity = 3000) {
    this.capacity = capacity
    this.time = new Float64Array(capacity)
  }

  /** Legt einen Kanal an, falls er noch nicht da ist. */
  ensure(key: string): Float32Array {
    let buffer = this.data.get(key)
    if (!buffer) {
      buffer = new Float32Array(this.capacity).fill(NaN)
      this.data.set(key, buffer)
    }
    return buffer
  }

  clear(): void {
    this.head = 0
    this.filled = 0
    this.now = 0
    this.lastPhase = 0
    this.marks = []
    this.data.forEach((buffer) => buffer.fill(NaN))
  }

  get length(): number {
    return this.filled
  }

  /**
   * Schreibt einen Abtastpunkt.
   *
   * `values` ist bereits ausgewertet — der Puffer kennt weder Simulation noch
   * Kanalkatalog. Das hält ihn prüfbar: man füttert Zahlen und liest Zahlen.
   */
  push(time: number, phase: number, values: Record<string, number>): void {
    /* Ein Zyklusanfang ist ein Rücksprung der Phase. Er wird als Marke
       festgehalten, weil man sonst im Zeitschrieb nicht sieht, wo ein Schritt
       aufhört — die Kurve läuft ja durch. */
    if (this.filled > 0 && phase < this.lastPhase) {
      this.mark(time, 'cycle')
    }
    this.lastPhase = phase

    this.time[this.head] = time
    for (const key of Object.keys(values)) {
      this.ensure(key)[this.head] = values[key]
    }

    this.head = (this.head + 1) % this.capacity
    this.filled = Math.min(this.filled + 1, this.capacity)
    this.now = time
  }

  /** Setzt eine Marke. */
  mark(time: number, kind: TraceMark['kind'], label?: string): void {
    this.marks.push({ time, kind, label })
    /* Marken, die längst aus dem Ring gelaufen sind, werden fallen gelassen —
       sonst wächst die Liste doch. Die Grenze ist grosszügig: die Zeit des
       ältesten noch gespeicherten Punktes. */
    const oldest = this.filled > 0 ? this.time[this.index(this.filled - 1)] : 0
    if (this.marks.length > 200) {
      this.marks = this.marks.filter((m) => m.time >= oldest)
    }
  }

  /** Index im Ring, `offset` Punkte vor dem jüngsten. */
  private index(offset: number): number {
    return (this.head - 1 - offset + this.capacity * 2) % this.capacity
  }

  /**
   * Zieht ein Zeitfenster heraus, auf ein festes Raster gelegt.
   *
   * WARUM UMGERASTERT WIRD: die Aufzeichnung läuft im Bildtakt und ist damit
   * nicht gleichmässig — ein ausgelasteter Rechner liefert 40 Punkte je
   * Sekunde, ein ruhiger 60. Ein Diagramm, das gleichmässige Abstände
   * unterstellt, würde daraus eine verzerrte Zeitachse machen. Hier wird
   * deshalb auf ein festes Raster interpoliert, und die Zeitachse stimmt.
   */
  window(seconds: number, endAt: number = this.now, samples = 480): TraceWindow {
    const to = endAt
    const from = to - seconds
    const series: Record<string, Float32Array> = {}
    for (const key of this.data.keys()) series[key] = new Float32Array(samples).fill(NaN)

    if (this.filled === 0) {
      return { from, to, series, samples, marks: [] }
    }

    /*
      Ein Durchgang vom ältesten zum jüngsten Punkt, das Raster zieht mit.
      Zwischen zwei Punkten wird linear interpoliert.

      VOR DEM ERSTEN UND NACH DEM LETZTEN PUNKT BLEIBT NaN. Das ist der
      Unterschied zwischen „hier war nichts" und „hier war der erste Wert" —
      der erste Versuch füllte den Anfang des Fensters mit dem ältesten Wert
      zurück und zeichnete damit eine waagerechte Linie in eine Zeit, in der
      noch gar nicht aufgezeichnet wurde.
    */
    const step = seconds / (samples - 1)
    const keys = Array.from(this.data.keys())

    let slot = 0
    let previous = -1

    for (let offset = this.filled - 1; offset >= 0 && slot < samples; offset--) {
      const current = this.index(offset)
      const t = this.time[current]
      if (t < from) {
        previous = current
        continue
      }
      if (t > to) break

      /* Rasterpunkte vor dem ersten Wert überspringen — ohne Vorgänger gibt es
         nichts zu interpolieren. */
      if (previous < 0) {
        while (slot < samples && from + slot * step < t) slot++
      }

      while (slot < samples && from + slot * step <= t) {
        const at = from + slot * step
        const t0 = previous >= 0 ? this.time[previous] : t
        const span = t - t0
        const k = span > 0 ? (at - t0) / span : 1
        for (const key of keys) {
          const buffer = this.data.get(key)!
          const a = previous >= 0 ? buffer[previous] : buffer[current]
          series[key][slot] = a * (1 - k) + buffer[current] * k
        }
        slot++
      }
      previous = current
    }

    const marks = this.marks
      .filter((m) => m.time >= from && m.time <= to)
      .map((m) => ({
        at: seconds > 0 ? (m.time - from) / seconds : 0,
        kind: m.kind,
        label: m.label,
      }))

    return { from, to, series, samples, marks }
  }
}

/** Die Fensterbreiten, die die Seite anbietet, in Sekunden. */
export const TRACE_WINDOWS = [2, 4, 8, 16] as const
